import styled from "@emotion/styled";
import { mq } from "@utils/media-query/mediaQuery";
import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { color, image } from "src/theme";

// STYLE: Search functionality
const SearchContainer = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "4rem",
  marginInline: "auto",
  maxWidth: "60rem",

  [mq("desktop")]: {
    marginBottom: "7rem",
  },
}));

const SearchWrapper = styled.div(() => ({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  gap: "1rem",
  minHeight: "10rem",
}));

const SearchInputWrapper = styled.div(({ theme, isError }) => ({
  position: "relative",
  width: "100%",
  maxWidth: "60rem",
  display: "flex",
  alignItems: "center",
  background: theme.color.white,
  borderRadius: "5rem",
  overflow: "hidden",
  border: `2px solid ${
    isError ? theme.color.red_color : theme.color.primary_color
  }`,
}));

const SearchInput = styled.input(({ theme }) => ({
  width: "100%",
  padding: "1.6rem 8rem 1.6rem 2.4rem",
  border: "none",
  outline: "none",
  fontSize: "1.4rem",
  fontFamily: theme.fontFamily.secondaryFont,
  color: theme.color.primary_grey,
  background: "transparent",
  fontWeight: theme.fontWeight.regular,

  "&::placeholder": {
    color: theme.color.medium_grey,
    fontSize: "1.6rem",
  },

  [mq("desktop")]: {
    padding: "2rem 9rem 2rem 3rem",
    fontSize: "1.8rem",

    "&::placeholder": {
      fontSize: "1.8rem",
    },
  },
}));

const IconsWrapper = styled.div(() => ({
  position: "absolute",
  right: "2rem",
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  gap: "0.2rem",
}));

const CloseLineIcon = styled.img(() => ({
  width: "2rem",
  height: "2rem",
  cursor: "pointer",

  [mq("desktop")]: {
    width: "2.4rem",
    height: "2.4rem",
  },
}));

const SearchIconButton = styled.button(({ theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "0.8rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  transition: "all 0.3s",

  "&:hover": {
    background: theme.color.primary_blur_color,
  },

  [mq("desktop")]: {
    right: "2.4rem",
    padding: "1rem",
  },
}));

const SearchIcon = styled.img(() => ({
  width: "2rem",
  height: "2rem",

  [mq("desktop")]: {
    width: "2.4rem",
    height: "2.4rem",
  },
}));

const Search = ({
  keyword,
  setKeyword,
  setPage,
  searchInput,
  setSearchInput,
}) => {
  const intl = useIntl();

  const [isError, setIsError] = useState(false);

  const handleSearchInputChange = useCallback(
    (e) => {
      setSearchInput(e.target.value);
    },
    [setSearchInput]
  );

  const handleSearch = useCallback(() => {
    const regex = /[{}[\]/?.,:;|)*~`!^\-_+<>@#$%&\\=()'"]/g;

    if (regex.test(searchInput)) {
      setIsError(true);
    } else {
      setIsError(false);
      setKeyword(searchInput);
      setPage(1);
    }
  }, [searchInput, setKeyword, setPage]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleReset = useCallback(() => {
    setSearchInput("");
    setKeyword("");
    setIsError(false);
  }, [setSearchInput, setKeyword]);

  return (
    <SearchContainer>
      <SearchWrapper>
        <SearchInputWrapper isError={isError}>
          <SearchInput
            id="keyword"
            type="text"
            placeholder={intl.formatMessage({
              id: "lang-search-placeholder",
            })}
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyDown={handleKeyPress}
          />

          <IconsWrapper>
            {searchInput && (
              <CloseLineIcon
                src={image.closeLine.default}
                alt="close icon"
                width={24}
                height={24}
                onClick={handleReset}
                style={{ color: "red" }}
              />
            )}

            <SearchIconButton onClick={handleSearch}>
              <SearchIcon src={image.searchIcon.default} alt="search icon" />
            </SearchIconButton>
          </IconsWrapper>
        </SearchInputWrapper>

        {isError && (
          <div style={{ color: color.red_color }}>
            {intl.formatMessage({ id: "lang-search-error" })}
          </div>
        )}
      </SearchWrapper>
    </SearchContainer>
  );
};

export default Search;
